import { buildProperty } from "@firecms/core";

// Shared field builders for page-content collections. A page collection holds
// one document whose properties are that page's sections, in page order.
//
// Length caps live here; firestore.rules only checks shape.

export const MAX = {
  eyebrow: 80,
  heading: 160,
  subheading: 600,
  paragraph: 2000,
  quote: 2000,
  attribution: 100,
  cardTitle: 120,
  cardBody: 2000,
  ctaLabel: 60,
};

/** The small pill of text that sits above a section heading. */
export const eyebrow = (description = "The small label in the coloured pill above the heading. Keep it to a few words.") =>
  buildProperty({
    name: "Label above heading",
    dataType: "string",
    description,
    validation: { max: MAX.eyebrow },
  });

/** The big hand-lettered section title. */
export const heading = (description = "The large title for this section. Short titles look best — long ones wrap onto extra lines.") =>
  buildProperty({
    name: "Heading",
    dataType: "string",
    description,
    validation: { max: MAX.heading },
  });

/** Optional lead line sitting between the heading and the body. */
export const subheading = (description = "One short line under the heading, before the paragraphs.") =>
  buildProperty({
    name: "Intro line",
    dataType: "string",
    multiline: true,
    description,
    validation: { max: MAX.subheading },
  });

/** A stack of prose paragraphs; can be added to, removed and reordered. */
export const paragraphs = (
  name = "Paragraphs",
  description = "Each box is one paragraph, shown in this order. Use ADD to write another, the handle to drag one up or down, and the bin to remove one.",
) =>
  buildProperty({
    name,
    dataType: "array",
    description,
    of: {
      dataType: "string",
      multiline: true,
      validation: { max: MAX.paragraph },
    },
  });

/** A highlighted pull quote. `attribution` is the "— Rose Stroh" line. */
export const quote = (withAttribution: boolean, name = "Highlighted quote") =>
  buildProperty({
    name,
    dataType: "map",
    expanded: true,
    properties: {
      text: {
        name: "Quote",
        dataType: "string",
        multiline: true,
        description: "Typed without quotation marks — the site adds them.",
        validation: { max: MAX.quote },
      },
      ...(withAttribution
        ? {
            attribution: {
              name: "Credited to",
              dataType: "string",
              description: "Shown after a dash, e.g. Rose Stroh.",
              validation: { max: MAX.attribution },
            },
          }
        : {}),
    },
  });

/**
 * The text on a button; its destination stays in the code. Pass a `name` where
 * a section has more than one button.
 */
export const ctaLabel = (
  description = "The wording on the button. Where it goes is set in the site itself.",
  name = "Button text",
) =>
  buildProperty({
    name,
    dataType: "string",
    description,
    validation: { max: MAX.ctaLabel },
  });

/**
 * A photo slot. Photos are cropped to fill a fixed shape, so each carries
 * `focusX`/`focusY` to aim the crop. Uploads are resized in the browser.
 */
export const photo = ({
  name = "Photo",
  description,
  storagePath,
  shape,
}: {
  name?: string;
  description: string;
  storagePath: string;
  /** Plain-language note about the shape this photo is cropped to. */
  shape: string;
}) =>
  buildProperty({
    name,
    dataType: "map",
    description,
    expanded: true,
    properties: {
      src: {
        name: "Picture",
        dataType: "string",
        description: `Drop in a new picture to replace the current one. ${shape}`,
        storage: {
          storagePath,
          acceptedFiles: ["image/jpeg", "image/png", "image/webp"],
          maxSize: 32 * 1024 * 1024,
          storeUrl: true,
          // Shrunk in the browser before upload — big enough to stay sharp on
          // a large screen, small enough that the page stays quick.
          imageResize: { maxWidth: 1600, maxHeight: 1600 },
          fileName: "{rand}_{file}",
        },
      },
      alt: {
        name: "Description of the picture",
        dataType: "string",
        description:
          "A short plain description of what is in the photo, e.g. Lucy asleep on the sofa. It is not shown on the page — it is read aloud to visitors who cannot see the image, and it is what search engines read.",
        validation: { max: 200 },
      },
      focusX: {
        name: "Focus — left to right",
        dataType: "number",
        description:
          "Which part to keep when the picture is cropped. 0 keeps the left edge, 50 the middle, 100 the right edge.",
        validation: { min: 0, max: 100 },
      },
      focusY: {
        name: "Focus — top to bottom",
        dataType: "number",
        description:
          "0 keeps the top of the picture, which is usually what you want for faces. 50 is the middle, 100 the bottom.",
        validation: { min: 0, max: 100 },
      },
    },
  });

/**
 * A row of photos with no text. Reorderable, since each entry carries its own
 * crop; the number of slots is fixed by the layout.
 */
export const photoRow = ({
  name,
  description,
  count,
  storagePath,
  shape,
}: {
  name: string;
  description: string;
  count: number;
  storagePath: string;
  shape: string;
}) =>
  buildProperty({
    name,
    dataType: "array",
    description: `${description} There are always ${count} — the layout is built around that, so they can be replaced and reordered, but not added to.`,
    canAddElements: false,
    sortable: true,
    validation: { min: count, max: count },
    of: photo({
      description: "One picture in the row.",
      storagePath,
      shape,
    }),
  });

/**
 * A row of cards. The count is fixed by the layout, and the site pairs each card
 * with its photo and styling by position, so entries can only be edited.
 */
export const cards = ({
  name,
  description,
  count,
  properties,
  previewProperties,
  sortable = false,
}: {
  name: string;
  description: string;
  count: number;
  properties: Record<string, any>;
  previewProperties?: string[];
  /** Only safe when nothing is paired to entries by position. */
  sortable?: boolean;
}) =>
  buildProperty({
    name,
    dataType: "array",
    description: `${description} There are always ${count} — the layout is built around that, so they can be edited${sortable ? " and reordered, but not added to." : " but not added to or reordered."}`,
    canAddElements: false,
    sortable,
    validation: { min: count, max: count },
    of: {
      dataType: "map",
      expanded: false,
      previewProperties,
      properties,
    },
  });

/** One section of the page, rendered as a titled panel in the form. */
export const section = (
  name: string,
  description: string,
  properties: Record<string, any>,
) =>
  buildProperty({
    name,
    dataType: "map",
    description,
    expanded: true,
    properties,
  });
